// ViewModalFieldsSection.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ViewModalFieldsSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';

/**
 * ViewModalFieldsSection
 *
 * Renders the selectable list of fields to show in the view.
 * 
 * @param {boolean} isVisible - Whether the section should be rendered.
 * @param {array} properties - The currently selected properties to show.
 * @param {function} setProperties - Function to update the selected properties.
 * @param {array} fieldOptions - Available fields that can be selected as properties.
 * 
 * @returns {ReactNode} The rendered component.
 */
const ViewModalFieldsSection = ({
    isVisible,
    properties,
    setProperties,
    fieldOptions,
    label = 'Visible Properties',
    hint = 'These fields will be displayed in each card in the view.',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [propertiesPopupOpen, setPropertiesPopupOpen] = useState(false);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [hoveredOptionKey, setHoveredOptionKey] = useState('');
    const [hoveredFieldIndex, setHoveredFieldIndex] = useState(null);

    // ----- REFS ----- //

    // Ref for the "Add Field" button to anchor the properties popup

    const addFieldAnchorRef = useRef(null);

    // ----- MEMOS ----- //

    const safeFieldOptions = useMemo(() => (
        Array.isArray(fieldOptions)
            ? fieldOptions.filter((option) => option?.value && option.value !== 'title')
            : []
    ), [fieldOptions]);

    const selectFieldOptions = useMemo(() => {
        const base = Array.isArray(safeFieldOptions) ? safeFieldOptions : [];
        const hasAutoNumber = base.some((option) => option?.value === 'autonumber');
        const hasAutoKey = base.some((option) => option?.value === 'autokey');
        const idOption = { value: 'autonumber', label: 'ID' };
        const keyOption = { value: 'autokey', label: 'Key' };
        const rootFieldOrder = ['autonumber', 'autokey', 'subtitle', 'dateCreated', 'dateModified'];

        const withId = hasAutoNumber ? base : [idOption, ...base];
        const withRoots = hasAutoKey ? withId : [keyOption, ...withId];

        const rootFields = [];
        const schemaFields = [];

        withRoots.forEach((option) => {
            const value = option?.value;
            if (rootFieldOrder.includes(value)) {
                rootFields.push(option);
                return;
            }
            schemaFields.push(option);
        });

        const orderedRoots = rootFieldOrder
            .map((rootKey) => rootFields.find((option) => option?.value === rootKey))
            .filter(Boolean);

        const sortedSchemaFields = schemaFields.sort((a, b) => (
            String(a?.label || a?.value || '').localeCompare(
                String(b?.label || b?.value || ''),
                undefined,
                { sensitivity: 'base' },
            )
        ));

        return [...orderedRoots, ...sortedSchemaFields];
    }, [safeFieldOptions]);

    const normalizedProperties = Array.isArray(properties) ? properties : [];

    const optionLabelByValue = useMemo(() => {
        const map = new Map();
        selectFieldOptions.forEach((option) => {
            map.set(option.value, option.label || option.value);
        });
        return map;
    }, [selectFieldOptions]);

    // ----- UTILITIES ----- //

    const isFieldIncluded = (fieldKey) => normalizedProperties.includes(fieldKey);

    const toggleField = (fieldKey) => {
        if (!fieldKey) return;
        setProperties((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const index = next.indexOf(fieldKey);

            if (index >= 0) {
                return next.filter((value) => value !== fieldKey);
            }

            next.push(fieldKey);
            return next;
        });
    };

    // ----- HANDLERS ----- //

    /**
     * handleAddField
     *
     * Handles add field.
     */
    const handleAddField = () => {
        if (selectFieldOptions.length === 0) return;
        setPropertiesPopupOpen((prev) => !prev);
    };

    /**
     * handleDragStart
     *
     * Handles drag start.
     *
     * @param {object} event - event.
     * @param {number} index - index.
     */
    const handleDragStart = (event, index) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
        setDraggingIndex(index);
        setDragOverIndex(index);
    };

    /**
     * handleDragOver
     *
     * Handles drag over.
     *
     * @param {object} event - event.
     * @param {number} index - index.
     */
    const handleDragOver = (event, index) => {
        event.preventDefault();
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    /**
     * handleDrop
     *
     * Handles drop.
     *
     * @param {object} event - event.
     * @param {number} dropIndex - dropIndex.
     */
    const handleDrop = (event, dropIndex) => {
        event.preventDefault();

        const fromData = event.dataTransfer.getData('text/plain');
        const parsedIndex = Number.parseInt(fromData, 10);
        const fromIndex = Number.isInteger(parsedIndex) ? parsedIndex : draggingIndex;

        if (!Number.isInteger(fromIndex)) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        if (fromIndex === dropIndex) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        setProperties((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            if (fromIndex < 0 || fromIndex >= next.length) return next;
            if (dropIndex < 0 || dropIndex >= next.length) return next;

            const [moved] = next.splice(fromIndex, 1);
            next.splice(dropIndex, 0, moved);
            return next;
        });

        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    /**
     * handleDragEnd
     *
     * Handles drag end.
     */
    const handleDragEnd = () => {
        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    if (!isVisible) return null;

    // ----- RENDER ----- //

    return (
        <ModalField>

            <div className="view-modal-section-header">

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                  ref={addFieldAnchorRef}
                >
                    <ModalLabel label={label} />
                    <ModalHint hint={hint} />
                </div>

                <div
                  style={{
                    display: 'inline-flex',
                  }}
                  ref={addFieldAnchorRef}
                >
                    <Button
                        iconKey="edit"
                      label="Edit"
                      fontSize="11px"
                      foregroundColor={theme.foreground}
                      height="30px"
                      padding="0 10px"
                      disabled={selectFieldOptions.length === 0}
                      onClick={handleAddField}
                    />
                </div>

            </div>

            <Popup
                anchorRef={addFieldAnchorRef}
                isOpen={propertiesPopupOpen}
                setOpen={setPropertiesPopupOpen}
            >

                {/* PROPERTIES POPUP */}
                <div
                    className="view-modal-properties-popup"
                >

                    {/* PROPERTIES POPUP EMPTY */}
                    {selectFieldOptions.length === 0 && (
                        <div
                            className="view-modal-properties-popup-empty"
                        >
                            No fields available.
                        </div>
                    )}

                    {selectFieldOptions.map((option) => {
                        const included = isFieldIncluded(option.value);
                        const eyeColor = included ? theme.foreground : theme.foregroundMuted;
                        const eyeHovered = hoveredOptionKey === option.value;

                        /**
                         * handleOptionClick
                         *
                         * Handles option click.
                         */
                        const handleOptionClick = () => {
                            toggleField(option.value);
                        };

                        return (
                            <PopupOption
                                key={option.value}
                              label={option.label}
                              labelColor={eyeColor}
                              selected={included}
                              rightContent={(
                                    <button
                                        type="button"
                                        className="view-modal-properties-eye-button"
                                        style={{
                                            backgroundColor: eyeHovered ? theme.backgroundHover : 'transparent',
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleOptionClick();
                                        }}
                                        onMouseEnter={() => setHoveredOptionKey(option.value)}
                                        onMouseLeave={() => setHoveredOptionKey('')}
                                    >
                                        <Icon
                                            iconKey={included ? 'visibility' : 'visibility_off'}
                                            width={17}
                                            height={17}
                                            color={eyeColor}
                                        />
                                    </button>
                                )}
                              onClick={handleOptionClick}                   />
                        );
                    })}
                </div>
            </Popup>

            {selectFieldOptions.length === 0 && (
                <div
                  className="view-modal-empty"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >No fields available.</div>
            )}

            {safeFieldOptions.length > 0 && normalizedProperties.length === 0 && (
                <ModalHint hint="All properties are visible by default. Use the Edit button to customize which properties are shown in this view." />
            )}

            {normalizedProperties.length > 0 && (
                <div className="view-modal-property-rows">
                    {normalizedProperties.map((fieldKey, index) => (
                        <div
                            className={`view-modal-property-item${draggingIndex === index ? ' dragging' : ''}${dragOverIndex === index ? ' drag-over' : ''}`}
                          style={{
                                backgroundColor: dragOverIndex === index
                                    ? theme.backgroundMuted
                                    : hoveredFieldIndex === index
                                        ? theme.backgroundHover
                                        : 'transparent',
                                borderColor: dragOverIndex === index ? theme.foregroundMuted : 'transparent',
                            }}
                          key={`view_property_${index}`}
                          draggable
                          onDragEnd={handleDragEnd}
                          onDragOver={(event) => handleDragOver(event, index)}
                          onDragStart={(event) => handleDragStart(event, index)}
                          onDrop={(event) => handleDrop(event, index)}
                          onMouseEnter={() => setHoveredFieldIndex(index)}
                          onMouseLeave={() => setHoveredFieldIndex(null)}
                        >
                            <div className="view-modal-property-item-handle">
                                <Icon
                                    iconKey="drag_indicator"
                                    width={16}
                                    height={16}
                                    color={theme.foregroundMuted}
                                />
                            </div>
                            <div
                                className="view-modal-property-item-label"
                              style={{
                                color: theme.foreground,
                              }}
                            >
                                {optionLabelByValue.get(fieldKey) || fieldKey}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </ModalField>
    );
};

// Dev Tools
ViewModalFieldsSection.displayName = 'ViewModalFieldsSection';

export default ViewModalFieldsSection;
